import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { GeneralPopup, PageHeader, PageWrapper, PopupTypes } from 'components/partials';
import { AutoCompleteField, Button, Checkbox, Label, Spinner, TextField } from '../components/widgets';
import FileDropzone from '../components/molecules/FileDropzone';
import Icon from '../components/foundations/Icon';
import { trackMixpanelEvent } from '../utils/general';
import toaster from '../utils/toaster';
import { deleteReport, getAdNetworks, getApps, getPlatforms, getUploadedReports, submitReport } from './service';
import { buildSubmitParams, toOptions } from './utils';
import {
    ACCEPTED_FILE_TYPES,
    DEFAULT_TEXT_FIELDS,
    HEADER_FIELDS,
    INSTRUCTION_KEYS,
    MIXPANEL_PREFIX,
    TRANSLATION_PREFIX,
} from './consts';
import css from './page.css';

const EMPTY_DICTIONARIES = { adnetworks: [], apps: [], platforms: [] };

function ManualUploadPage({ translate }) {
    const [dictionaries, setDictionaries] = useState(EMPTY_DICTIONARIES);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    const [showInstructions, setShowInstructions] = useState(false);
    const [defaultOptions, setDefaultOptions] = useState({ adnetwork: null, app: null, platform: null });
    const [defaultTexts, setDefaultTexts] = useState({});
    const [headers, setHeaders] = useState({});
    const [file, setFile] = useState(null);
    const [isRefund, setIsRefund] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const refreshReports = () => getUploadedReports().then(setReports);

    useEffect(() => {
        let cancelled = false;
        Promise.all([getAdNetworks(), getApps(), getPlatforms(), getUploadedReports()])
            .then(([adnetworks, apps, platforms, uploadedReports]) => {
                if (cancelled) {
                    return;
                }
                setDictionaries({ adnetworks, apps, platforms });
                setReports(uploadedReports);
            })
            .catch(() => !cancelled && setLoadError(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, []);

    const adNetworkOptions = useMemo(() => toOptions(dictionaries.adnetworks), [dictionaries.adnetworks]);
    const appOptions = useMemo(() => toOptions(dictionaries.apps), [dictionaries.apps]);
    const platformOptions = useMemo(() => toOptions(dictionaries.platforms), [dictionaries.platforms]);

    const setDefaultOption = (key, option) => setDefaultOptions(prev => ({ ...prev, [key]: option }));

    const submit = () => {
        trackMixpanelEvent(MIXPANEL_PREFIX, 'Upload Button Clicked');
        if (!file) {
            return;
        }

        setSubmitting(true);
        const queryString = buildSubmitParams({
            defaults: {
                adnetwork: defaultOptions.adnetwork && defaultOptions.adnetwork.value,
                app: defaultOptions.app && defaultOptions.app.value,
                platform: defaultOptions.platform && defaultOptions.platform.value,
                ...defaultTexts,
            },
            headers,
            filename: file.name,
            isRefund,
        });

        submitReport(file, queryString)
            .then(response => {
                if (response.status !== 0) {
                    toaster.error(response.value.result);
                    return null;
                }
                toaster.success(translate(`${TRANSLATION_PREFIX}.SUBMIT_SUCCESS`));
                setFile(null);
                return refreshReports();
            })
            .catch(() => toaster.error(translate(`${TRANSLATION_PREFIX}.SUBMIT_ERROR`)))
            .finally(() => setSubmitting(false));
    };

    const confirmDelete = () => {
        setDeleting(true);
        deleteReport(reportToDelete.public_id)
            .then(() => {
                toaster.success(translate(`${TRANSLATION_PREFIX}.DELETE_SUCCESS`));
                setReportToDelete(null);
                return refreshReports();
            })
            .catch(value => toaster.error((value && value.result) || translate(`${TRANSLATION_PREFIX}.DELETE_ERROR`)))
            .finally(() => setDeleting(false));
    };

    if (loading) {
        return <Spinner show expanded />;
    }

    if (loadError) {
        return (
            <PageWrapper>
                <PageHeader text={`${TRANSLATION_PREFIX}.PAGE_TITLE`} />
                <div className={css.notes}>
                    <Translate id={`${TRANSLATION_PREFIX}.LOAD_ERROR`} />
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper className={css.wrapper}>
            <PageHeader text={`${TRANSLATION_PREFIX}.PAGE_TITLE`} />

            <button type="button" className={css.instructionsToggle} onClick={() => setShowInstructions(open => !open)}>
                <Translate id={`${TRANSLATION_PREFIX}.INSTRUCTIONS`} />
                <Icon name={showInstructions ? 'chevron-up' : 'chevron-down'} interactive={false} />
            </button>
            {showInstructions && (
                <div className={css.instructions}>
                    <Translate id={`${TRANSLATION_PREFIX}.INSTRUCTIONS_INTRO`} />
                    <ul className={css.instructionsList}>
                        {INSTRUCTION_KEYS.map(key => (
                            <li key={key}>
                                <Translate id={key} />
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className={css.section}>
                <Label type="h2" text={`${TRANSLATION_PREFIX}.DEFAULTS`} />
                <div className={css.sectionFields}>
                    <AutoCompleteField
                        controlled
                        searchable
                        clearable
                        isMulti={false}
                        label={`${TRANSLATION_PREFIX}.DEFAULT_ADNETWORK`}
                        placeholder={`${TRANSLATION_PREFIX}.SELECT_PLACEHOLDER`}
                        options={adNetworkOptions}
                        value={defaultOptions.adnetwork}
                        onChange={option => setDefaultOption('adnetwork', option)}
                    />
                    <AutoCompleteField
                        controlled
                        searchable
                        clearable
                        isMulti={false}
                        label={`${TRANSLATION_PREFIX}.DEFAULT_APP`}
                        placeholder={`${TRANSLATION_PREFIX}.SELECT_PLACEHOLDER`}
                        options={appOptions}
                        value={defaultOptions.app}
                        onChange={option => setDefaultOption('app', option)}
                    />
                    <AutoCompleteField
                        controlled
                        searchable
                        clearable
                        isMulti={false}
                        label={`${TRANSLATION_PREFIX}.DEFAULT_PLATFORM`}
                        placeholder={`${TRANSLATION_PREFIX}.SELECT_PLACEHOLDER`}
                        options={platformOptions}
                        value={defaultOptions.platform}
                        onChange={option => setDefaultOption('platform', option)}
                    />
                    {DEFAULT_TEXT_FIELDS.map(({ key, label }) => (
                        <TextField
                            key={key}
                            label={label}
                            placeholder={label}
                            value={defaultTexts[key] || ''}
                            onChange={value => setDefaultTexts(prev => ({ ...prev, [key]: value }))}
                        />
                    ))}
                </div>
            </div>

            <div className={css.section}>
                <Label type="h2" text={`${TRANSLATION_PREFIX}.REPORT_HEADERS`} />
                <div className={css.sectionFields}>
                    {HEADER_FIELDS.map(({ key, label, placeholder }) => (
                        <TextField
                            key={key}
                            label={label}
                            placeholder={placeholder}
                            value={headers[key] || ''}
                            onChange={value => setHeaders(prev => ({ ...prev, [key]: value }))}
                        />
                    ))}
                </div>
            </div>

            <div className={css.dropzone}>
                <FileDropzone
                    clearable
                    value={file}
                    disabled={submitting}
                    acceptableTypes={ACCEPTED_FILE_TYPES}
                    onChange={setFile}
                />
            </div>

            {/* The checkbox and the button are both inline-level, so they need a block container of their own. */}
            <div className={css.actions}>
                <Checkbox
                    label={<Translate id={`${TRANSLATION_PREFIX}.IS_REFUND`} />}
                    checked={isRefund}
                    onChange={setIsRefund}
                />
                <Button
                    type="primary"
                    level="level1"
                    text={`${TRANSLATION_PREFIX}.SUBMIT`}
                    disabled={!file || submitting}
                    showSpinner={submitting}
                    onClick={submit}
                />
            </div>

            <div className={css.notes}>
                <div>
                    <Translate id={`${TRANSLATION_PREFIX}.NOTE_MAX_SIZE`} />
                </div>
                <div>
                    <Translate id={`${TRANSLATION_PREFIX}.NOTE_BIG_FILES`} />
                </div>
            </div>

            <div className={css.reportsSection}>
                <Label type="h2" text={`${TRANSLATION_PREFIX}.PREVIOUS_REPORTS`} />
                {reports.length === 0 ? (
                    <div className={css.emptyReports}>
                        <Translate id={`${TRANSLATION_PREFIX}.NO_REPORTS`} />
                    </div>
                ) : (
                    <table className={css.reportsTable}>
                        <thead>
                            <tr>
                                <th>{translate(`${TRANSLATION_PREFIX}.COLUMN_CREATED_AT`)}</th>
                                <th>{translate(`${TRANSLATION_PREFIX}.COLUMN_FILENAME`)}</th>
                                <th>{translate(`${TRANSLATION_PREFIX}.COLUMN_STATUS`)}</th>
                                <th>{translate(`${TRANSLATION_PREFIX}.COLUMN_DATES`)}</th>
                                <th aria-label={translate(`${TRANSLATION_PREFIX}.DELETE`)} />
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map(report => (
                                <tr key={report.public_id}>
                                    <td>{report.created_at}</td>
                                    <td>{report.filename}</td>
                                    <td>{report.status}</td>
                                    <td>{`${report.min_stats_date} .. ${report.max_stats_date}`}</td>
                                    <td>
                                        <Button
                                            type="flat"
                                            level="level2"
                                            text={`${TRANSLATION_PREFIX}.DELETE`}
                                            onClick={() => setReportToDelete(report)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <GeneralPopup
                open={!!reportToDelete}
                type={PopupTypes.DELETE}
                title={`${TRANSLATION_PREFIX}.DELETE_MODAL_TITLE`}
                text={`${TRANSLATION_PREFIX}.DELETE_MODAL_TEXT`}
                textReplace={{ filename: reportToDelete ? reportToDelete.filename : '' }}
                acceptText={`${TRANSLATION_PREFIX}.DELETE`}
                rejectText="STATIC.BUTTONS.CANCEL"
                showSpinner={deleting}
                onAccept={confirmDelete}
                onReject={() => setReportToDelete(null)}
            />
        </PageWrapper>
    );
}

ManualUploadPage.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ManualUploadPage);
